<template>
  <section
    v-if="errors.length"
    class="step-errors"
    :class="{
      'is-slim': slim,
    }"
  >
    <header class="step-errors__header">
      <form-section-label tag="h3">Looks like we have a problem!</form-section-label>
      <form-section-question>
        {{ pluralize('question', errors.length, true) }}
        need{{ errors.length === 1 ? 's' : '' }}
        your attention in order to continue to the next step
      </form-section-question>
    </header>
    <div
      v-for="error in errors"
      :key="error.questionId"
      class="step-errors__row"
    >
      <label class="step-errors__label">
        <span v-html="error.title" />
      </label>
      <div class="step-errors__actions">
        <v-btn
          v-add-zuko-tag="`resolve_btn ${componentKey}`"
          rounded
          dark
          class="btn-resolve-error"
          color="navy-light"
          data-test="resolve_btn"
          @click.prevent="onResolve(error)"
        >
          Resolve
        </v-btn>
      </div>
    </div>
  </section>
</template>

<script>
import pluralize from 'pluralize';

export default {
  name: 'step-errors',
  props: {
    stepRef: {
      type: Object,
      required: true,
    },
    stepId: {
      type: String,
      required: true,
    },
    componentKey: {
      type: String,
      required: true,
    },
    questions: {
      type: Array,
      required: true,
    },
    questionObservers: {
      type: Object,
      required: true,
    },
    multiformQuestionsDetails: {
      type: Array,
      default: () => [],
    },
    slim: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    generalQuestionErrors () {
      return this.questions
        .filter(question => !question.hasMultiForm && this.questionObservers[question.questionId]?.failed)
        .map(question => ({
          questionId: question.questionId,
          title: this.stepRef?.$refs[question.questionId]?.[0]?.dynamicTitle || question.params.title,
        }));
    },
    multiformQuestionErrors () {
      return this.questions
        .filter(question => question.hasMultiForm)
        .reduce((failedQuestions, segment) => {
          Object.entries(this.questionObservers[segment.questionId]?.fields).forEach(([fieldKey, fieldValue]) => {
            const questionDetails = this.multiformQuestionsDetails.find(details => details.field === fieldKey);
            if (fieldValue.failed && questionDetails) {
              failedQuestions.push({ ...questionDetails, hasMultiForm: true });
            }
          });

          return failedQuestions;
        }, [])
        .sort((a, b) => a.index - b.index);
    },
    errors () {
      return [...this.generalQuestionErrors, ...this.multiformQuestionErrors];
    },
  },
  methods: {
    getScrollToElementId (error) {
      return error.hasMultiForm ? `#${error.questionId}` : `#question-${this.stepId}-${error.questionId}`;
    },
    onResolve (error) {
      if (this.$listeners.resolve) {
        this.$emit('resolve', error);
        return;
      }

      this.$scrollTo(this.getScrollToElementId(error));
    },
  },
  setup () {
    return { pluralize };
  },
};
</script>

<style lang="scss" scoped>
.step-errors {
  border-radius: 6px;
  background: $clr-white;
  overflow: hidden;
  border: 1px solid var(--v-error-base, $clr-error);
  padding: 0 36px;

  --padding: 8px;

  &.is-slim {
    --padding: 4px;
  }

  &__header {
    padding: calc(var(--padding) * 3) 0 calc(var(--padding) * 4);
  }

  &__row {
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;
    display: flex;
    justify-content: space-between;
    border-top: 1px solid $clr-outline;
    padding: calc(var(--padding) * 4) 0;
    align-items: center;
  }
}
</style>
